import { Button, Input } from '@massilia/ui'
import { Loader2, Tag, X } from 'lucide-react'
import { useState } from 'react'

import { formatPrice } from '@/utils/format-price'

interface PromoCodeSlotProps {
  /** Applied code (uppercase) or null when none is applied yet */
  appliedCode: null | string
  /** Total discount in EUR (already per-passenger on the server) */
  discountEuros: null | number
  error: null | string
  isVerifying: boolean
  onRemove: () => void
  onVerify: (code: string) => void
  /** Billable passengers the discount applies to (babies excluded) */
  passengerCount: number
}

/**
 * Form-agnostic promo-code slot shared by the informations (step 2) and checkout pages.
 * Manages its own input buffer; the applied discount comes from the session.
 */
export function PromoCodeSlot({
  appliedCode,
  discountEuros,
  error,
  isVerifying,
  onRemove,
  onVerify,
  passengerCount,
}: PromoCodeSlotProps) {
  const [code, setCode] = useState('')

  if (appliedCode && discountEuros) {
    // Show the per-passenger breakdown when the discount divides evenly across the
    // billable passengers (i.e. it wasn't clamped to the subtotal).
    const unit = passengerCount > 1 ? discountEuros / passengerCount : null
    const showBreakdown = unit !== null && Number.isInteger(unit)

    return (
      <div className="flex w-full flex-col gap-0.5">
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-2">
            <Tag className="h-3.5 w-3.5 text-success-600" />
            <span className="text-sm font-medium text-success-600">
              {appliedCode.toUpperCase()}
            </span>
            <button
              className="text-gray-light-500 transition-colors hover:text-gray-light-700"
              onClick={onRemove}
              type="button"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <span className="text-sm font-medium text-success-600">
            - {formatPrice(discountEuros)}
          </span>
        </div>
        {showBreakdown && (
          <span className="text-xs text-gray-light-500">
            {formatPrice(unit)} × {passengerCount} voyageurs
          </span>
        )}
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col gap-1.5">
      <div className="flex flex-row items-center gap-2">
        <Input
          className="flex-1"
          onChange={(e) => setCode(e.target.value)}
          placeholder="Code promo"
          value={code}
        />
        <Button
          disabled={isVerifying || !code.trim()}
          onClick={() => onVerify(code)}
          type="button"
          variant="outline"
        >
          {isVerifying ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <span className="text-xs font-medium">Appliquer</span>
          )}
        </Button>
      </div>
      {error && <p className="text-xs font-medium text-error-600">{error}</p>}
    </div>
  )
}
